import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

interface QRScannerProps {
  onScanSuccess: (data: any) => void;
  onScanError: (error: string) => void;
}

const QRScanner: React.FC<QRScannerProps> = ({ onScanSuccess, onScanError }) => {
  const [qrCode, setQrCode] = useState('');
  const [status, setStatus] = useState('hadir');
  const [catatan, setCatatan] = useState('');
  const [loading, setLoading] = useState(false);
  const [scannedData, setScannedData] = useState<any>(null);
  const [confirmCardOpen, setConfirmCardOpen] = useState(false);
  const [pendingCheckin, setPendingCheckin] = useState<{qr: string, status: string, catatan: string} | null>(null);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const { panitia } = useAuth();
  const [pesertaInfo, setPesertaInfo] = useState<any>(null);

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!qrCode.trim()) {
      onScanError('QR Code tidak boleh kosong');
      return;
    }
    setLoading(true);
    setPesertaInfo(null);
    setScannedData(null);
    try {
      const res = await axios.post('https://event.petirs.id/backend/api/get-peserta-by-qr.php', { qr_code: qrCode.trim() });
      if (res.data.status === 'success') {
        setPesertaInfo(res.data.data);
        setPendingCheckin({ qr: qrCode.trim(), status, catatan });
        setConfirmCardOpen(true);
        // Don't call onScanSuccess here since QRScanner handles its own flow
      } else {
        onScanError(res.data.message || 'Peserta tidak ditemukan');
      }
    } catch (err: any) {
      onScanError(err.response?.data?.message || 'Gagal mengambil data peserta');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCheckin = async () => {
    if (!pendingCheckin) return;
    setCheckinLoading(true);
    try {
              const response = await axios.post('https://event.petirs.id/backend/api/verify-qr.php', {
        qr_code: pendingCheckin.qr,
        id_panitia: panitia?.id_panitia,
        status: pendingCheckin.status,
        catatan: pendingCheckin.catatan
      });
      if (response.data.success) {
        const dataToShow = {
          peserta: response.data.data,
          checkin: {
            status: pendingCheckin.status,
            waktu_checkin: response.data.data.waktu_checkin,
            panitia_nama: panitia?.nama_panitia || 'Panitia',
            catatan: pendingCheckin.catatan
          }
        };
        setScannedData(dataToShow);
        // Don't call onScanSuccess here since QRScanner handles its own flow
        setQrCode('');
        setCatatan('');
        setConfirmCardOpen(false);
        setPendingCheckin(null);
        setPesertaInfo(null);
      } else {
        onScanError(response.data.message || 'Verifikasi QR Code gagal');
      }
    } catch (error: any) {
      onScanError(error.response?.data?.message || 'Terjadi kesalahan saat verifikasi QR Code');
    } finally {
      setCheckinLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmCardOpen(false);
    setPendingCheckin(null);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Scan QR Code Peserta
      </h3>
      
      <form onSubmit={handleManualSubmit} className="space-y-4">
        <div>
          <label htmlFor="qrCode" className="block text-sm font-medium text-gray-700 mb-2">
            QR Code / ID Peserta
          </label>
          <input
            type="text"
            id="qrCode"
            value={qrCode}
            onChange={(e) => setQrCode(e.target.value)}
            placeholder="Masukkan QR Code atau ID Peserta"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
            Status Kehadiran
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="hadir">Hadir</option>
            <option value="terlambat">Terlambat</option>
            <option value="tidak_hadir">Tidak Hadir</option>
          </select>
        </div>

        <div>
          <label htmlFor="catatan" className="block text-sm font-medium text-gray-700 mb-2">
            Catatan (Opsional)
          </label>
          <textarea
            id="catatan"
            value={catatan}
            onChange={(e) => setCatatan(e.target.value)}
            placeholder="Catatan tambahan..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Memproses...' : 'Verifikasi & Check-in Peserta'}
        </button>
      </form>

      {/* Tampilkan data peserta yang di-scan */}
      {scannedData && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <h4 className="text-lg font-semibold text-green-800 mb-3">Data Peserta yang Di-scan:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-green-700"><strong>Nama:</strong> {scannedData.peserta.nama_pendaftar}</p>
              <p className="text-sm text-green-700"><strong>ID Peserta:</strong> {scannedData.peserta.id_pendaftar}</p>
              <p className="text-sm text-green-700"><strong>No. Peserta:</strong> {scannedData.peserta.no_peserta}</p>
              <p className="text-sm text-green-700"><strong>QR Code:</strong> {qrCode}</p>
              <p className="text-sm text-green-700"><strong>Event:</strong> {scannedData.peserta.event}</p>
              <p className="text-sm text-green-700"><strong>Paket:</strong> {scannedData.peserta.paket}</p>
              <p className="text-sm text-green-700"><strong>Instansi:</strong> {scannedData.peserta.instansi}</p>
            </div>
            <div>
              <p className="text-sm text-green-700"><strong>Status:</strong> 
                <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                  scannedData.checkin.status === 'hadir' 
                    ? 'bg-green-100 text-green-800'
                    : scannedData.checkin.status === 'terlambat'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {scannedData.checkin.status === 'hadir' ? 'Hadir' : 
                   scannedData.checkin.status === 'terlambat' ? 'Terlambat' : 'Tidak Hadir'}
                </span>
              </p>
              <p className="text-sm text-green-700"><strong>Waktu Check-in:</strong> {new Date(scannedData.checkin.waktu_checkin).toLocaleString('id-ID')}</p>
              <p className="text-sm text-green-700"><strong>Panitia:</strong> {scannedData.checkin.panitia_nama}</p>
              {scannedData.checkin.catatan && (
                <p className="text-sm text-green-700"><strong>Catatan:</strong> {scannedData.checkin.catatan}</p>
              )}
            </div>
          </div>
          <button
            onClick={() => setScannedData(null)}
            className="mt-3 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
          >
            Tutup
          </button>
        </div>
      )}

      <div className="mt-4 p-3 bg-blue-50 rounded-md">
        <p className="text-sm text-blue-800">
          <strong>Petunjuk:</strong> Masukkan QR Code atau ID Peserta untuk verifikasi dan check-in otomatis. Sistem akan selalu melakukan insert baru ke tabel check-in, sehingga setiap scan akan menambah history check-in peserta.
        </p>
      </div>

      {/* Floating Card/Dialog konfirmasi checkin */}
      {confirmCardOpen && pendingCheckin && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-[350px] max-w-full border border-gray-200">
            <h3 className="text-base font-semibold mb-3">Konfirmasi Checkin Peserta</h3>
            {checkinLoading ? (
              <div className="text-center py-6">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Memproses checkin...</p>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <div className="text-sm text-gray-700 mb-1">QR Code: <span className="font-mono">{pendingCheckin.qr}</span></div>
                  <div className="text-gray-600 text-sm">Status: {pendingCheckin.status}</div>
                  {pendingCheckin.catatan && <div className="text-gray-600 text-sm">Catatan: {pendingCheckin.catatan}</div>}
                  {pesertaInfo && (
                    <div className="mt-2 p-2 bg-gray-50 rounded">
                      <div className="text-sm text-gray-800"><strong>Nama:</strong> {pesertaInfo.nama_pendaftar}</div>
                      <div className="text-sm text-gray-800"><strong>Instansi:</strong> {pesertaInfo.instansi}</div>
                      <div className="text-sm text-gray-800"><strong>Status Menginap:</strong> {pesertaInfo.status_menginap}</div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={handleCancelConfirm}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    disabled={checkinLoading}
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleConfirmCheckin}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    disabled={checkinLoading}
                  >
                    Checkin
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QRScanner; 